import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { parceirosApi, parceriasApi } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Search, Loader2, Handshake, Eye, TrendingUp, AlertTriangle, CheckCircle2, XCircle, QrCode, BarChart3, Pencil, Trash2, KeyRound, FileDown } from 'lucide-react';

const isoDate = (d) => d.toISOString().slice(0, 10);
const periodoDatas = (preset) => {
  const hoje = new Date();
  if (preset === 'hoje') return [isoDate(hoje), isoDate(hoje)];
  if (preset === '7d') { const d = new Date(hoje); d.setDate(d.getDate() - 7); return [isoDate(d), isoDate(hoje)]; }
  if (preset === '30d') { const d = new Date(hoje); d.setDate(d.getDate() - 30); return [isoDate(d), isoDate(hoje)]; }
  if (preset === 'mes_atual') return [isoDate(new Date(hoje.getFullYear(), hoje.getMonth(), 1)), isoDate(hoje)];
  if (preset === 'mes_anterior') {
    const ini = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
    const fim = new Date(hoje.getFullYear(), hoje.getMonth(), 0);
    return [isoDate(ini), isoDate(fim)];
  }
  return [isoDate(new Date(hoje.getFullYear(), hoje.getMonth(), 1)), isoDate(hoje)];
};

const STATUS_COR = {
  Ativo: 'bg-green-100 text-green-700', Inativo: 'bg-gray-100 text-gray-600',
  Bloqueado: 'bg-red-100 text-red-700', Pendente: 'bg-amber-100 text-amber-700',
};
const FATURA_COR = {
  'Aguardando Pagamento': 'bg-amber-100 text-amber-800', Paga: 'bg-green-100 text-green-700',
  Vencida: 'bg-red-100 text-red-700', Cancelada: 'bg-gray-100 text-gray-500',
  Aberta: 'bg-blue-100 text-blue-700', Fechada: 'bg-gray-100 text-gray-700',
};

export const emptyParceiro = {
  tipo: 'Imobiliária', pessoa: 'PJ', razao_social: '', nome_fantasia: '', cnpj_cpf: '',
  responsavel: '', cargo: '', telefone: '', whatsapp: '', email: '',
  endereco: { cep: '', rua: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '' },
  status: 'Ativo', data_inicio: '', responsavel_comercial: '', origem: '', observacoes_internas: '', risco: 'Baixo',
};

export const ParceiroFormDialog = ({ open, onOpenChange, initial, onSaved, tipos = [], statusOptions = [] }) => {
  const [form, setForm] = useState({ ...emptyParceiro });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(initial ? { ...emptyParceiro, ...initial, endereco: { ...emptyParceiro.endereco, ...(initial.endereco || {}) } } : { ...emptyParceiro });
  }, [open, initial]);
  const set = (p) => setForm((f) => ({ ...f, ...p }));
  const setEnd = (p) => setForm((f) => ({ ...f, endereco: { ...f.endereco, ...p } }));

  const salvar = async () => {
    setSaving(true);
    try {
      const { id, codigo, limite_credito, credito_utilizado, credito_disponivel, created_at, nome,
        qtd_imoveis, qtd_solicitacoes, qtd_os_concluidas, qtd_usuarios, faturamento, ultima_solicitacao,
        indicadores, sugestao_limite, faturamento_config, ...payload } = form;
      if (initial?.id) await parceirosApi.update(initial.id, payload);
      else await parceirosApi.create(payload);
      toast.success(initial?.id ? 'Parceiro atualizado' : 'Parceiro cadastrado');
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao salvar parceiro');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial?.id ? 'Editar parceiro' : 'Cadastrar parceiro'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Tipo *</Label>
            <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={form.tipo} onChange={(e) => set({ tipo: e.target.value })} data-testid="parceiro-tipo">
              {(tipos.length ? tipos : ['Imobiliária']).map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <Label>Pessoa</Label>
            <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={form.pessoa} onChange={(e) => set({ pessoa: e.target.value })}>
              <option value="PJ">Jurídica</option><option value="PF">Física</option>
            </select>
          </div>
          <div><Label>Razão social</Label><Input value={form.razao_social} onChange={(e) => set({ razao_social: e.target.value })} data-testid="parceiro-razao" /></div>
          <div><Label>Nome fantasia *</Label><Input value={form.nome_fantasia} onChange={(e) => set({ nome_fantasia: e.target.value })} data-testid="parceiro-fantasia" /></div>
          <div><Label>{form.pessoa === 'PJ' ? 'CNPJ' : 'CPF'}</Label><Input value={form.cnpj_cpf} onChange={(e) => set({ cnpj_cpf: e.target.value })} /></div>
          <div><Label>Responsável</Label><Input value={form.responsavel} onChange={(e) => set({ responsavel: e.target.value })} /></div>
          <div><Label>Cargo</Label><Input value={form.cargo} onChange={(e) => set({ cargo: e.target.value })} /></div>
          <div><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={(e) => set({ whatsapp: e.target.value })} /></div>
          <div><Label>Telefone</Label><Input value={form.telefone} onChange={(e) => set({ telefone: e.target.value })} /></div>
          <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} /></div>
          <div className="col-span-2 grid grid-cols-4 gap-2">
            <div><Label>CEP</Label><Input value={form.endereco.cep} onChange={(e) => setEnd({ cep: e.target.value })} /></div>
            <div className="col-span-2"><Label>Rua</Label><Input value={form.endereco.rua} onChange={(e) => setEnd({ rua: e.target.value })} /></div>
            <div><Label>Nº</Label><Input value={form.endereco.numero} onChange={(e) => setEnd({ numero: e.target.value })} /></div>
          </div>
          <div className="col-span-2 grid grid-cols-4 gap-2">
            <div><Label>Complemento</Label><Input value={form.endereco.complemento} onChange={(e) => setEnd({ complemento: e.target.value })} /></div>
            <div><Label>Bairro</Label><Input value={form.endereco.bairro} onChange={(e) => setEnd({ bairro: e.target.value })} /></div>
            <div><Label>Cidade</Label><Input value={form.endereco.cidade} onChange={(e) => setEnd({ cidade: e.target.value })} /></div>
            <div><Label>UF</Label><Input value={form.endereco.estado} onChange={(e) => setEnd({ estado: e.target.value })} maxLength={2} /></div>
          </div>
          <div>
            <Label>Status</Label>
            <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={form.status} onChange={(e) => set({ status: e.target.value })} data-testid="parceiro-status">
              {(statusOptions.length ? statusOptions : ['Ativo', 'Inativo', 'Bloqueado', 'Pendente']).map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div><Label>Data de início</Label><Input type="date" value={form.data_inicio} onChange={(e) => set({ data_inicio: e.target.value })} /></div>
          <div><Label>Responsável comercial Tenance</Label><Input value={form.responsavel_comercial} onChange={(e) => set({ responsavel_comercial: e.target.value })} /></div>
          <div>
            <Label>Origem</Label>
            <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={form.origem} onChange={(e) => set({ origem: e.target.value })}>
              <option value="">Selecione...</option>
              {['Prospecção', 'Indicação', 'Site', 'Instagram', 'Google', 'Outro'].map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <Label>Risco</Label>
            <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={form.risco} onChange={(e) => set({ risco: e.target.value })}>
              {['Baixo', 'Médio', 'Alto'].map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div className="col-span-2"><Label>Observações internas</Label><Textarea rows={2} value={form.observacoes_internas} onChange={(e) => set({ observacoes_internas: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="bg-[#2B7A3D] hover:bg-[#236632]" disabled={saving || !(form.nome_fantasia || form.razao_social)} onClick={salvar} data-testid="salvar-parceiro">
            {saving ? <Loader2 className="animate-spin" size={16} /> : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CardStat = ({ label, value, sub }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-3">
    <div className="text-lg font-bold text-[#3D4F5F]">{value}</div>
    <div className="text-[11px] text-gray-500 font-medium">{label}</div>
    {sub && <div className="text-[10px] text-gray-400">{sub}</div>}
  </div>
);

export default function Parceiros() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [lista, setLista] = useState(null);
  const [busca, setBusca] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editParceiro, setEditParceiro] = useState(null);
  const [delParceiro, setDelParceiro] = useState(null);
  const [aumentos, setAumentos] = useState([]);
  const [faturas, setFaturas] = useState([]);
  const [financeiro, setFinanceiro] = useState([]);
  const [filtroFin, setFiltroFin] = useState('todos');
  const [config, setConfig] = useState(null);
  const [analise, setAnalise] = useState(null);
  const [valorOutro, setValorOutro] = useState('');
  const [periodoPreset, setPeriodoPreset] = useState('mes_atual');
  const [periodoCustom, setPeriodoCustom] = useState({ inicio: '', fim: '' });
  const [relatorio, setRelatorio] = useState(null);
  const [editFatura, setEditFatura] = useState(null);
  const [delFatura, setDelFatura] = useState(null);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [recusaDialog, setRecusaDialog] = useState({ open: false, sol: null, motivo: '' });

  const load = useCallback(() => {
    parceirosApi.stats().then((r) => setStats(r.data)).catch(() => {});
    parceirosApi.list().then((r) => setLista(r.data)).catch(() => setLista([]));
    parceirosApi.listAumentos().then((r) => setAumentos(r.data)).catch(() => {});
    parceirosApi.listFaturas().then((r) => setFaturas(r.data)).catch(() => {});
    parceirosApi.getConfig().then((r) => setConfig(r.data)).catch(() => {});
    parceriasApi.listar().then((r) => setSolicitacoes(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    parceirosApi.financeiro(filtroFin).then((r) => setFinanceiro(r.data)).catch(() => {});
  }, [filtroFin]);

  useEffect(() => {
    let inicio, fim;
    if (periodoPreset === 'personalizado') {
      if (!periodoCustom.inicio || !periodoCustom.fim) return;
      inicio = periodoCustom.inicio; fim = periodoCustom.fim;
    } else {
      [inicio, fim] = periodoDatas(periodoPreset);
    }
    parceirosApi.relatorio(inicio, fim).then((r) => setRelatorio(r.data)).catch(() => {});
  }, [periodoPreset, periodoCustom]);

  const copiarPixFatura = async (f) => {
    try {
      const r = await parceirosApi.pixFatura(f.id);
      const link = `${window.location.origin}/pagar/${r.data.id}`;
      await navigator.clipboard?.writeText(link);
      toast.success(`Link PIX da ${f.codigo} copiado: ${link}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao gerar PIX da fatura');
    }
  };

  const analisar = async (sid, acao, valor = null) => {
    try {
      await parceirosApi.analisarAumento(sid, { acao, valor_aprovado: valor });
      toast.success(acao === 'aprovar' ? 'Limite aprovado' : 'Solicitação recusada');
      setAnalise(null); setValorOutro('');
      load();
    } catch (err) {
      toast.error('Erro ao analisar');
    }
  };

  const aprovarSolicitacao = async (s) => {
    try {
      const r = await parceriasApi.aprovar(s.id);
      if (r.data.destino_tipo === 'parceiro') {
        toast.success('Parceiro criado! Defina o limite e crie o login do usuário.');
        navigate(`/sistema/parceiros/${r.data.destino_id}`);
      } else {
        toast.success('Prestador aprovado e cadastrado em Colaboradores!');
        load();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao aprovar solicitação');
    }
  };

  const recusarSolicitacao = async () => {
    try {
      await parceriasApi.recusar(recusaDialog.sol.id, recusaDialog.motivo);
      toast.success('Solicitação recusada');
      setRecusaDialog({ open: false, sol: null, motivo: '' });
      load();
    } catch {
      toast.error('Erro ao recusar solicitação');
    }
  };

  const baixarPdfFatura = async (f) => {
    try {
      const r = await parceirosApi.faturaPdf(f.id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${f.codigo}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro ao gerar PDF da fatura');
    }
  };

  const marcarFaturaPaga = async (f) => {
    try {
      await parceirosApi.atualizarFaturaStatus(f.id, { status: 'Paga' });
      toast.success(`${f.codigo} marcada como paga — crédito liberado`);
      load();
      parceirosApi.financeiro(filtroFin).then((r) => setFinanceiro(r.data));
    } catch { toast.error('Erro ao atualizar fatura'); }
  };

  const salvarFatura = async () => {
    try {
      await parceirosApi.editarFatura(editFatura.id, {
        desconto: parseFloat(editFatura.desconto) || 0,
        acrescimos: parseFloat(editFatura.acrescimos) || 0,
        vencimento: editFatura.vencimento || null,
      });
      toast.success(`${editFatura.codigo} atualizada`);
      setEditFatura(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao editar fatura');
    }
  };

  const excluirFatura = async () => {
    try {
      await parceirosApi.excluirFatura(delFatura.id);
      toast.success(`${delFatura.codigo} excluída`);
      setDelFatura(null);
      load();
      parceirosApi.financeiro(filtroFin).then((r) => setFinanceiro(r.data));
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao excluir fatura');
    }
  };

  const excluirParceiro = async () => {
    try {
      await parceirosApi.delete(delParceiro.id);
      toast.success(`Parceiro ${delParceiro.nome} excluído`);
      setDelParceiro(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao excluir parceiro');
      setDelParceiro(null);
    }
  };

  const salvarConfig = async () => {
    try {
      await parceirosApi.updateConfig({ limite_padrao: parseFloat(config.limite_padrao), bloquear_inadimplente: config.bloquear_inadimplente, faixas_desconto: config.faixas_desconto });
      toast.success('Configuração salva');
    } catch { toast.error('Erro ao salvar configuração'); }
  };

  const filtrados = (lista || []).filter((p) => {
    const b = busca.toLowerCase();
    return !b || [p.codigo, p.nome, p.razao_social, p.responsavel, p.email, p.whatsapp, p.endereco?.cidade].some((c) => (c || '').toLowerCase().includes(b));
  });

  const pendentes = aumentos.filter((a) => a.status === 'Pendente');

  return (
    <div className="space-y-5" data-testid="pagina-parceiros">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-[#3D4F5F] flex items-center gap-2"><Handshake className="text-[#2B7A3D]" /> Gestão de Parceiros</h1>
        <Button className="bg-[#2B7A3D] hover:bg-[#236632]" onClick={() => setShowForm(true)} data-testid="btn-cadastrar-parceiro">
          <Plus size={16} className="mr-1" /> Cadastrar Parceiro
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          <CardStat label="Total" value={stats.total} />
          <CardStat label="Ativos" value={stats.ativos} />
          <CardStat label="Inativos" value={stats.inativos} />
          <CardStat label="Bloqueados" value={stats.bloqueados} />
          <CardStat label="Solicitações no mês" value={stats.solicitacoes_mes} />
          <CardStat label="OS concluídas" value={stats.os_concluidas} />
          <CardStat label="Faturamento" value={formatCurrency(stats.faturamento)} />
          <CardStat label="Vencidos" value={formatCurrency(stats.valores_vencidos)} sub={`Crédito em uso: ${formatCurrency(stats.credito_utilizado)}`} />
        </div>
      )}

      <Tabs defaultValue="parceiros">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="parceiros" data-testid="tab-parceiros">Parceiros</TabsTrigger>
          <TabsTrigger value="solicitacoes" data-testid="tab-solicitacoes">
            Solicitações{solicitacoes.filter((s) => s.status === 'Pendente').length > 0 && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">{solicitacoes.filter((s) => s.status === 'Pendente').length}</span>}
          </TabsTrigger>
          <TabsTrigger value="aumentos" data-testid="tab-aumentos">
            Aumentos de limite {pendentes.length > 0 && <span className="ml-1.5 bg-red-500 text-white text-[10px] rounded-full px-1.5">{pendentes.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="faturas" data-testid="tab-faturas">Faturas</TabsTrigger>
          <TabsTrigger value="financeiro" data-testid="tab-financeiro">Financeiro</TabsTrigger>
          <TabsTrigger value="relatorios" data-testid="tab-relatorios">Relatórios</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config">Configurações</TabsTrigger>
        </TabsList>

        <TabsContent value="parceiros" className="space-y-3">
          <div className="relative max-w-sm">
            <Search size={15} className="absolute left-3 top-3 text-gray-400" />
            <Input className="pl-9" placeholder="Buscar parceiro..." value={busca} onChange={(e) => setBusca(e.target.value)} data-testid="busca-parceiros" />
          </div>
          {!lista ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" /></div> : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-gray-50 text-xs text-gray-500">
                  <tr>
                    {['Código', 'Parceiro', 'Tipo', 'Responsável', 'Cidade', 'Imóveis', 'Solic.', 'OS concl.', 'Faturamento', 'Limite disp.', 'Última solic.', 'Status', 'Ações'].map((h) => (
                      <th key={h} className="px-3 py-2.5 text-left font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map((p) => (
                    <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/sistema/parceiros/${p.id}`)} data-testid={`linha-parceiro-${p.codigo}`}>
                      <td className="px-3 py-2.5 font-mono text-xs">{p.codigo}</td>
                      <td className="px-3 py-2.5 font-semibold text-[#3D4F5F]">{p.nome}</td>
                      <td className="px-3 py-2.5 text-xs">{p.tipo}</td>
                      <td className="px-3 py-2.5 text-xs">{p.responsavel || '-'}</td>
                      <td className="px-3 py-2.5 text-xs">{p.endereco?.cidade || '-'}</td>
                      <td className="px-3 py-2.5 text-center">{p.qtd_imoveis}</td>
                      <td className="px-3 py-2.5 text-center">{p.qtd_solicitacoes}</td>
                      <td className="px-3 py-2.5 text-center">{p.qtd_os_concluidas}</td>
                      <td className="px-3 py-2.5">{formatCurrency(p.faturamento)}</td>
                      <td className="px-3 py-2.5 font-medium text-[#2B7A3D]">{formatCurrency(p.credito_disponivel)}</td>
                      <td className="px-3 py-2.5 text-xs">{p.ultima_solicitacao ? formatDate(p.ultima_solicitacao.slice(0, 10)) : '-'}</td>
                      <td className="px-3 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_COR[p.status]}`}>{p.status}</span></td>
                      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-0.5">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Ver perfil completo" onClick={() => navigate(`/sistema/parceiros/${p.id}`)} data-testid={`ver-parceiro-${p.codigo}`}><Eye size={15} className="text-gray-500" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Editar parceiro" onClick={() => setEditParceiro(p)} data-testid={`editar-parceiro-${p.codigo}`}><Pencil size={14} className="text-blue-600" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Acesso ao Portal (login do parceiro)" onClick={() => navigate(`/sistema/parceiros/${p.id}?tab=usuarios`)} data-testid={`acesso-parceiro-${p.codigo}`}><KeyRound size={14} className="text-[#2B7A3D]" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Excluir parceiro" onClick={() => setDelParceiro(p)} data-testid={`excluir-parceiro-${p.codigo}`}><Trash2 size={14} className="text-red-500" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtrados.length === 0 && <tr><td colSpan={13} className="px-3 py-8 text-center text-gray-400 text-sm">Nenhum parceiro encontrado</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="solicitacoes" className="space-y-3">
          <div className="table-fit">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="px-3 py-2">Tipo</th>
                  <th className="px-3 py-2">Nome / Empresa</th>
                  <th className="px-3 py-2">WhatsApp</th>
                  <th className="px-3 py-2 hidden lg:table-cell">Cidade</th>
                  <th className="px-3 py-2 hidden xl:table-cell">Detalhes</th>
                  <th className="px-3 py-2 hidden lg:table-cell">Data</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Ações</th>
                </tr>
              </thead>
              <tbody>
                {solicitacoes.slice().sort((a, b) => (a.status === 'Pendente' ? -1 : 1) - (b.status === 'Pendente' ? -1 : 1)).map((s) => (
                  <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50/60" data-testid={`solicitacao-${s.id}`}>
                    <td className="px-3 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.tipo_cadastro === 'parceiro' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                        {s.tipo_cadastro === 'parceiro' ? 'Empresa' : 'Prestador'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 font-medium">
                      {s.razao_social || s.nome}
                      {s.razao_social && <div className="text-xs text-gray-500">{s.nome}{s.tipo_empresa ? ` · ${s.tipo_empresa}` : ''}</div>}
                      {s.cnpj && <div className="text-[11px] text-gray-400">CNPJ: {s.cnpj}</div>}
                    </td>
                    <td className="px-3 py-2.5">
                      <a href={`https://wa.me/55${(s.telefone || '').replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-[#2B7A3D] hover:underline font-medium" data-testid={`whatsapp-solicitacao-${s.id}`}>
                        {s.telefone}
                      </a>
                    </td>
                    <td className="px-3 py-2.5 hidden lg:table-cell">{s.cidade || '—'}</td>
                    <td className="px-3 py-2.5 hidden xl:table-cell text-xs text-gray-500 max-w-[220px] truncate" title={s.observacoes}>{s.observacoes || '—'}</td>
                    <td className="px-3 py-2.5 hidden lg:table-cell text-xs text-gray-500">{s.created_at ? formatDate(s.created_at.slice(0, 10)) : ''}</td>
                    <td className="px-3 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.status === 'Pendente' ? 'bg-amber-100 text-amber-700' : s.status === 'Aprovada' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>{s.status}</span>
                      {s.status === 'Recusada' && s.motivo_recusa && <div className="text-[10px] text-gray-400 mt-0.5">{s.motivo_recusa}</div>}
                    </td>
                    <td className="px-3 py-2.5">
                      {s.status === 'Pendente' && (
                        <div className="flex gap-1">
                          <Button size="sm" className="text-xs h-7 bg-[#2B7A3D] hover:bg-[#236632]" onClick={() => aprovarSolicitacao(s)} data-testid={`aprovar-solicitacao-${s.id}`}>Aprovar</Button>
                          <Button size="sm" variant="outline" className="text-xs h-7 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setRecusaDialog({ open: true, sol: s, motivo: '' })} data-testid={`recusar-solicitacao-${s.id}`}>Recusar</Button>
                        </div>
                      )}
                      {s.status === 'Aprovada' && s.tipo_cadastro === 'parceiro' && s.destino_id && (
                        <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => navigate(`/sistema/parceiros/${s.destino_id}`)}>Ver parceiro</Button>
                      )}
                    </td>
                  </tr>
                ))}
                {solicitacoes.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400 text-sm">Nenhuma solicitação recebida. Divulgue a página "Seja parceiro" do site.</td></tr>}
              </tbody>
            </table>
          </div>

          <AlertDialog open={recusaDialog.open} onOpenChange={(v) => !v && setRecusaDialog({ open: false, sol: null, motivo: '' })}>
            <AlertDialogContent data-testid="dialog-recusar-solicitacao">
              <AlertDialogHeader>
                <AlertDialogTitle>Recusar solicitação de {recusaDialog.sol?.razao_social || recusaDialog.sol?.nome}?</AlertDialogTitle>
                <AlertDialogDescription>A solicitação será arquivada. Nenhum aviso é enviado ao solicitante.</AlertDialogDescription>
              </AlertDialogHeader>
              <Input placeholder="Motivo (opcional)" value={recusaDialog.motivo} onChange={(e) => setRecusaDialog({ ...recusaDialog, motivo: e.target.value })} data-testid="input-motivo-recusa" />
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={recusarSolicitacao} data-testid="confirmar-recusa">Recusar</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        <TabsContent value="aumentos" className="space-y-3">
          {aumentos.length === 0 ? <div className="text-sm text-gray-500 py-8 text-center">Nenhuma solicitação de aumento.</div> : aumentos.map((a) => (
            <div key={a.id} className="bg-white rounded-xl border border-gray-200 p-4" data-testid={`aumento-${a.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-semibold text-[#3D4F5F] flex items-center gap-2">
                    <TrendingUp size={16} className="text-[#2B7A3D]" /> {a.parceiro_nome}
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${a.status === 'Pendente' ? 'bg-amber-100 text-amber-700' : a.status === 'Aprovado' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{a.status}</span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Limite atual <b>{formatCurrency(a.limite_atual)}</b> → solicitado <b className="text-[#2B7A3D]">{formatCurrency(a.valor_solicitado)}</b>
                    {a.valor_aprovado != null && a.status === 'Aprovado' && <> · aprovado <b>{formatCurrency(a.valor_aprovado)}</b></>}
                  </div>
                  {a.motivo && <div className="text-xs text-gray-500 mt-1">Motivo: {a.motivo}</div>}
                  <div className="text-[11px] text-gray-400 mt-1">
                    Por {a.usuario_nome} em {a.created_at ? new Date(a.created_at).toLocaleString('pt-BR') : ''}
                    {a.analisado_por && ` · analisado por ${a.analisado_por}`}
                  </div>
                  <div className="flex gap-3 text-[11px] text-gray-500 mt-2 flex-wrap">
                    <span>OS: {a.indicadores.os_total}</span><span>Concluídas: {a.indicadores.os_concluidas}</span>
                    <span>Faturamento: {formatCurrency(a.indicadores.faturamento)}</span>
                    <span>Pagas: {a.indicadores.faturas_pagas}</span>
                    <span className={a.indicadores.faturas_vencidas > 0 ? 'text-red-600 font-semibold' : ''}>Vencidas: {a.indicadores.faturas_vencidas}</span>
                  </div>
                </div>
                {a.status === 'Pendente' && (
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" className="bg-[#2B7A3D] hover:bg-[#236632]" onClick={() => analisar(a.id, 'aprovar')} data-testid={`aprovar-aumento-${a.id}`}>
                      <CheckCircle2 size={14} className="mr-1" /> Aprovar {formatCurrency(a.valor_solicitado)}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setAnalise(a)}>Aprovar outro valor</Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => analisar(a.id, 'recusar')}>
                      <XCircle size={14} className="mr-1" /> Recusar
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="faturas" className="space-y-3">
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-gray-50 text-xs text-gray-500">
                <tr>{['Código', 'Parceiro', 'Período', 'OS', 'Subtotal', 'Desconto', 'Total', 'Vencimento', 'Status', 'Ações'].map((h) => <th key={h} className="px-3 py-2.5 text-left font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody>
                {faturas.map((f) => (
                  <tr key={f.id} className="border-t border-gray-100" data-testid={`fatura-admin-${f.codigo}`}>
                    <td className="px-3 py-2.5 font-mono text-xs">{f.codigo}</td>
                    <td className="px-3 py-2.5">{f.parceiro_nome}</td>
                    <td className="px-3 py-2.5 text-xs">{formatDate(f.periodo_inicio)} - {formatDate(f.periodo_fim)}</td>
                    <td className="px-3 py-2.5 text-center">{f.ordens.length}</td>
                    <td className="px-3 py-2.5">{formatCurrency(f.subtotal)}</td>
                    <td className="px-3 py-2.5 text-green-600">{f.desconto > 0 ? `-${formatCurrency(f.desconto)}` : '-'}</td>
                    <td className="px-3 py-2.5 font-semibold">{formatCurrency(f.total)}</td>
                    <td className="px-3 py-2.5 text-xs">{formatDate(f.vencimento)}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-col gap-0.5">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold w-fit ${FATURA_COR[f.status]}`}>{f.status}</span>
                        {f.pagamento_informado && f.status !== 'Paga' && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold w-fit bg-amber-100 text-amber-700" title={`Informado por ${f.pagamento_informado_por}`} data-testid={`pill-pagamento-informado-${f.codigo}`}>
                            💬 Pagamento informado
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex gap-1">
                        {['Aguardando Pagamento', 'Vencida', 'Aberta', 'Fechada'].includes(f.status) && (
                          <>
                            <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => marcarFaturaPaga(f)} data-testid={`pagar-fatura-${f.codigo}`}>Marcar paga</Button>
                            <Button size="sm" variant="outline" className="text-xs h-7 text-[#2B7A3D]" title="Copiar link de pagamento PIX" onClick={() => copiarPixFatura(f)} data-testid={`pix-fatura-${f.codigo}`}>
                              <QrCode size={13} className="mr-1" /> PIX
                            </Button>
                            <Button size="sm" variant="outline" className="text-xs h-7 w-7 p-0" title="Editar (desconto/acréscimo/vencimento)" onClick={() => setEditFatura({ ...f, desconto: f.desconto || '', acrescimos: f.acrescimos || '', vencimento: f.vencimento || '' })} data-testid={`editar-fatura-${f.codigo}`}>
                              <Pencil size={13} />
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="outline" className="text-xs h-7 w-7 p-0 text-[#3D4F5F]" title="Baixar PDF da fatura" onClick={() => baixarPdfFatura(f)} data-testid={`pdf-fatura-${f.codigo}`}>
                          <FileDown size={13} />
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs h-7 w-7 p-0 text-red-600 hover:text-red-700" title="Excluir fatura" onClick={() => setDelFatura(f)} data-testid={`excluir-fatura-${f.codigo}`}>
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {faturas.length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-gray-400 text-sm">Nenhuma fatura. Gere faturas no perfil do parceiro.</td></tr>}
              </tbody>
            </table>
          </div>

          {/* Editar fatura (desconto/acréscimo/vencimento) */}
          <Dialog open={!!editFatura} onOpenChange={(v) => !v && setEditFatura(null)}>
            <DialogContent className="max-w-sm" data-testid="dialog-editar-fatura">
              <DialogHeader><DialogTitle>Editar {editFatura?.codigo}</DialogTitle></DialogHeader>
              {editFatura && (
                <div className="space-y-3">
                  <div className="text-sm text-gray-600">Subtotal: <b>{formatCurrency(editFatura.subtotal)}</b></div>
                  <div>
                    <Label className="text-xs">Desconto (R$)</Label>
                    <Input type="number" min="0" step="0.01" value={editFatura.desconto} onChange={(e) => setEditFatura({ ...editFatura, desconto: e.target.value })} data-testid="input-fatura-desconto" />
                  </div>
                  <div>
                    <Label className="text-xs">Acréscimos (R$)</Label>
                    <Input type="number" min="0" step="0.01" value={editFatura.acrescimos} onChange={(e) => setEditFatura({ ...editFatura, acrescimos: e.target.value })} data-testid="input-fatura-acrescimos" />
                  </div>
                  <div>
                    <Label className="text-xs">Vencimento</Label>
                    <Input type="date" value={(editFatura.vencimento || '').slice(0, 10)} onChange={(e) => setEditFatura({ ...editFatura, vencimento: e.target.value })} data-testid="input-fatura-vencimento" />
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2.5 text-sm">
                    Novo total: <b className="text-[#2B7A3D]">{formatCurrency((editFatura.subtotal || 0) - (parseFloat(editFatura.desconto) || 0) + (parseFloat(editFatura.acrescimos) || 0))}</b>
                  </div>
                  <p className="text-[11px] text-gray-400">Se o total mudar, o PIX anterior é descartado e um novo é gerado com o valor atualizado.</p>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditFatura(null)}>Cancelar</Button>
                <Button className="bg-[#2B7A3D] hover:bg-[#236632]" onClick={salvarFatura} data-testid="salvar-fatura">Salvar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Excluir fatura */}
          <AlertDialog open={!!delFatura} onOpenChange={(v) => !v && setDelFatura(null)}>
            <AlertDialogContent data-testid="dialog-excluir-fatura">
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir {delFatura?.codigo}?</AlertDialogTitle>
                <AlertDialogDescription>
                  As OS voltam a ficar sem fatura (podem ser refaturadas) e as cobranças PIX desta fatura são descartadas.
                  {delFatura?.status === 'Paga' && ' Como esta fatura está PAGA: a receita volta para "pendente" no Financeiro e o crédito do parceiro é re-reservado.'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={excluirFatura} data-testid="confirmar-excluir-fatura">Excluir fatura</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        <TabsContent value="financeiro" className="space-y-3">
          <select className="h-9 border border-gray-300 rounded-md px-2 text-sm" value={filtroFin} onChange={(e) => setFiltroFin(e.target.value)} data-testid="filtro-financeiro">
            <option value="todos">Todos</option>
            <option value="com_saldo">Com saldo utilizado</option>
            <option value="proximos_limite">Próximos do limite (≥80%)</option>
            <option value="limite_atingido">Limite atingido</option>
            <option value="inadimplentes">Inadimplentes</option>
          </select>
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[750px]">
              <thead className="bg-gray-50 text-xs text-gray-500">
                <tr>{['Parceiro', 'Limite', 'Utilizado', 'Disponível', '% uso', 'Em aberto', 'Vencido', 'Próx. venc.', 'Alertas'].map((h) => <th key={h} className="px-3 py-2.5 text-left font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody>
                {financeiro.map((r) => (
                  <tr key={r.parceiro_id} className="border-t border-gray-100 cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/sistema/parceiros/${r.parceiro_id}`)}>
                    <td className="px-3 py-2.5 font-medium">{r.nome} <span className="text-[10px] text-gray-400">{r.codigo}</span></td>
                    <td className="px-3 py-2.5">{formatCurrency(r.limite)}</td>
                    <td className="px-3 py-2.5 text-amber-600">{formatCurrency(r.utilizado)}</td>
                    <td className="px-3 py-2.5 text-[#2B7A3D] font-medium">{formatCurrency(r.disponivel)}</td>
                    <td className="px-3 py-2.5">{r.pct_utilizado}%</td>
                    <td className="px-3 py-2.5">{formatCurrency(r.em_aberto)}</td>
                    <td className={`px-3 py-2.5 ${r.vencido > 0 ? 'text-red-600 font-semibold' : ''}`}>{formatCurrency(r.vencido)}</td>
                    <td className="px-3 py-2.5 text-xs">{r.proximo_vencimento ? formatDate(r.proximo_vencimento) : '-'}</td>
                    <td className="px-3 py-2.5">
                      {r.alertas.map((al, i) => <div key={i} className="flex items-center gap-1 text-[11px] text-red-600"><AlertTriangle size={11} />{al}</div>)}
                    </td>
                  </tr>
                ))}
                {financeiro.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-400 text-sm">Nenhum parceiro no filtro</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="relatorios" className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <BarChart3 size={18} className="text-[#2B7A3D]" />
            <h3 className="text-sm font-semibold text-[#3D4F5F]">Desempenho dos Parceiros</h3>
            <select className="h-9 border border-gray-300 rounded-md px-2 text-sm" value={periodoPreset} onChange={(e) => setPeriodoPreset(e.target.value)} data-testid="relatorio-periodo">
              <option value="hoje">Hoje</option>
              <option value="7d">Últimos 7 dias</option>
              <option value="30d">Últimos 30 dias</option>
              <option value="mes_atual">Mês atual</option>
              <option value="mes_anterior">Mês anterior</option>
              <option value="personalizado">Personalizado</option>
            </select>
            {periodoPreset === 'personalizado' && (
              <>
                <Input type="date" className="h-9 w-auto" value={periodoCustom.inicio} onChange={(e) => setPeriodoCustom({ ...periodoCustom, inicio: e.target.value })} data-testid="relatorio-inicio" />
                <Input type="date" className="h-9 w-auto" value={periodoCustom.fim} onChange={(e) => setPeriodoCustom({ ...periodoCustom, fim: e.target.value })} data-testid="relatorio-fim" />
              </>
            )}
            {relatorio && <span className="text-xs text-gray-400">{formatDate(relatorio.periodo.inicio)} a {formatDate(relatorio.periodo.fim)}</span>}
          </div>

          {!relatorio ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" /></div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="relatorio-indicadores">
                <CardStat label="Solicitações" value={relatorio.indicadores.solicitacoes} />
                <CardStat label="OS concluídas" value={relatorio.indicadores.os_concluidas} />
                <CardStat label="Cancelamentos" value={relatorio.indicadores.cancelamentos} />
                <CardStat label="Faturamento" value={formatCurrency(relatorio.indicadores.faturamento)} />
                <CardStat label="Ticket médio" value={formatCurrency(relatorio.indicadores.ticket_medio)} />
                <CardStat label="Recebido" value={formatCurrency(relatorio.indicadores.recebido)} />
                <CardStat label="Pendente" value={formatCurrency(relatorio.indicadores.pendente)} />
                <CardStat label="Vencido" value={formatCurrency(relatorio.indicadores.vencido)} />
              </div>

              <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm min-w-[800px]" data-testid="relatorio-ranking">
                  <thead className="bg-gray-50 text-xs text-gray-500">
                    <tr>{['#', 'Parceiro', 'Faturamento (pago)', 'OS no período', 'Valor em OS', 'Ticket médio', 'Recorrência', 'Pontualidade', 'Limite atual'].map((h) => <th key={h} className="px-3 py-2.5 text-left font-semibold">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {relatorio.ranking.map((r, i) => (
                      <tr key={r.parceiro_id} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/sistema/parceiros/${r.parceiro_id}`)}>
                        <td className="px-3 py-2.5 font-bold text-gray-400">{i + 1}º</td>
                        <td className="px-3 py-2.5 font-medium text-[#3D4F5F]">{r.nome} <span className="text-[10px] text-gray-400">{r.codigo}</span></td>
                        <td className="px-3 py-2.5 font-semibold text-[#2B7A3D]">{formatCurrency(r.faturamento)}</td>
                        <td className="px-3 py-2.5 text-center">{r.qtd_os}</td>
                        <td className="px-3 py-2.5">{formatCurrency(r.valor_os)}</td>
                        <td className="px-3 py-2.5">{formatCurrency(r.ticket_medio)}</td>
                        <td className="px-3 py-2.5 text-center">{r.recorrencia_meses} {r.recorrencia_meses === 1 ? 'mês' : 'meses'}</td>
                        <td className="px-3 py-2.5">
                          {r.pontualidade_pct == null ? <span className="text-gray-400 text-xs">sem pagamentos</span> : (
                            <span className={`font-semibold ${r.pontualidade_pct >= 90 ? 'text-green-600' : r.pontualidade_pct >= 70 ? 'text-amber-600' : 'text-red-600'}`}>{r.pontualidade_pct}%</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">{formatCurrency(r.limite_credito)}</td>
                      </tr>
                    ))}
                    {relatorio.ranking.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-400 text-sm">Nenhum parceiro</td></tr>}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-400">Use pontualidade e recorrência como apoio para decisões de aumento de limite. Faturamento considera faturas pagas no período; "Valor em OS" soma as OS criadas no período.</p>
            </>
          )}
        </TabsContent>

        <TabsContent value="config">
          {config && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 max-w-lg space-y-4">
              <div>
                <Label>Limite de crédito padrão para novos parceiros (R$)</Label>
                <Input type="number" value={config.limite_padrao} onChange={(e) => setConfig({ ...config, limite_padrao: e.target.value })} data-testid="config-limite-padrao" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={config.bloquear_inadimplente} onChange={(e) => setConfig({ ...config, bloquear_inadimplente: e.target.checked })} data-testid="config-bloquear-inadimplente" />
                Bloquear uso de crédito quando houver cobrança vencida
              </label>
              <div>
                <Label className="mb-1 block">Faixas de desconto por volume (mensal)</Label>
                {config.faixas_desconto.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 mb-1.5 text-sm">
                    <span className="text-xs text-gray-500 w-10">Até</span>
                    <Input className="h-8" type="number" value={f.ate ?? ''} placeholder="sem teto"
                      onChange={(e) => { const fx = [...config.faixas_desconto]; fx[i] = { ...fx[i], ate: e.target.value === '' ? null : parseFloat(e.target.value) }; setConfig({ ...config, faixas_desconto: fx }); }} />
                    <Input className="h-8 w-20" type="number" value={f.pct ?? ''} placeholder="negoc."
                      onChange={(e) => { const fx = [...config.faixas_desconto]; fx[i] = { ...fx[i], pct: e.target.value === '' ? null : parseFloat(e.target.value) }; setConfig({ ...config, faixas_desconto: fx }); }} />
                    <span className="text-xs text-gray-500">%</span>
                  </div>
                ))}
                <p className="text-[11px] text-gray-400">Faixa sem % = negociação personalizada. Faixas de referência de limite: {(config.faixas_limite || []).map((f) => `${f.nome} ${formatCurrency(f.valor)}`).join(' · ')}</p>
              </div>
              <Button className="bg-[#2B7A3D] hover:bg-[#236632]" onClick={salvarConfig} data-testid="salvar-config">Salvar configurações</Button>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ParceiroFormDialog open={showForm} onOpenChange={setShowForm} initial={null} onSaved={load} tipos={stats?.tipos || []} statusOptions={stats?.status_options || []} />
      <ParceiroFormDialog open={!!editParceiro} onOpenChange={(v) => !v && setEditParceiro(null)} initial={editParceiro} onSaved={load} tipos={stats?.tipos || []} statusOptions={stats?.status_options || []} />

      {/* Excluir parceiro */}
      <AlertDialog open={!!delParceiro} onOpenChange={(v) => !v && setDelParceiro(null)}>
        <AlertDialogContent data-testid="dialog-excluir-parceiro">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir {delParceiro?.nome}?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove o parceiro, seus usuários de acesso e imóveis. Parceiros com OS ou faturas no histórico não podem ser excluídos — nesse caso, altere o status para Inativo/Bloqueado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={excluirParceiro} data-testid="confirmar-excluir-parceiro">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Aprovar outro valor */}
      <Dialog open={!!analise} onOpenChange={(v) => !v && setAnalise(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Aprovar outro valor</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">{analise?.parceiro_nome} solicitou {analise && formatCurrency(analise.valor_solicitado)}.</p>
          <div><Label>Novo limite aprovado (R$)</Label><Input type="number" value={valorOutro} onChange={(e) => setValorOutro(e.target.value)} data-testid="input-outro-valor" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnalise(null)}>Cancelar</Button>
            <Button className="bg-[#2B7A3D] hover:bg-[#236632]" disabled={!valorOutro} onClick={() => analisar(analise.id, 'aprovar', parseFloat(valorOutro))} data-testid="confirmar-outro-valor">Aprovar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
